import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import {
  ArrowDown01,
  ArrowDown10,
  ArrowDownAZ,
  ArrowDownNarrowWide,
  ArrowDownWideNarrow,
  ArrowDownZA,
  ArrowUpDown,
  Edit,
  GripVertical,
  LoaderCircle,
  LucideAngularModule,
  Minus,
  Sparkles,
} from 'lucide-angular';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RouteParams } from '@app/shared/abstract/route-params';
import { SelectComponent } from '../../form/select/select.component';
import { FormControl } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { Column } from './column';
import { AbstractEntity } from '@app/shared/abstract/abstract-entity';
import { NgClass, NgComponentOutlet, NgStyle, NgTemplateOutlet } from '@angular/common';
import { PageMetadata } from '@app/shared/abstract/page-metadata';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { distinctUntilChanged, skip } from 'rxjs';
import { TemplateRef } from '@angular/core';

@Component({
  selector: 'app-data-table',
  imports: [LucideAngularModule, SelectComponent, RouterLink, NgClass, NgComponentOutlet, NgTemplateOutlet, BadgeComponent, NgStyle, CdkDropList, CdkDrag, CdkDragHandle],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css',
  standalone: true,
})
export class DataTableComponent<T extends AbstractEntity> implements OnChanges, OnInit, OnDestroy {
  @Input() dataPage?: PagedEntities<T>;
  @Input() title?: string;
  @Input() columns: Column<T>[] = [];
  get visibleColumns(): Column<T>[] {
    return this.columns.filter(col => !col.hidden);
  }
  get mobileVisibleColumns(): Column<T>[] {
    return this.visibleColumns.filter(col => !col.mobileHidden);
  }
  get mobilePrimaryColumn(): Column<T> | undefined {
    return this.mobileVisibleColumns[0];
  }
  get mobileSecondaryColumns(): Column<T>[] {
    return this.mobileVisibleColumns.slice(1);
  }
  @Input() paginate = true;
  @Input() noDataPlaceholder = true;
  @Input() mobileCardView = false;
  @Input() mobileCompact = false;
  @Input() mobileFlatShell = false;
  @Input() flatShell = false;
  @Input() rowActions: RowAction<T>[] = [];
  @Input() rowRouterLinkTemplate?: string;
  @Input() hover = true;
  @Input() loading = false;
  @Input() selectedRowId?: string | number;
  @Input() highlightSelectedRow = true;
  @Input() selectedRowClass = 'bg-blue-50 dark:bg-blue-950/40 ring-2 ring-blue-300 dark:ring-blue-700';
  @Input() rowClickable = false;
  @Input() selectionEnabled = false;
  @Input() selectedRowIds = new Set<number>();
  @Input() expandedRowTemplate?: TemplateRef<any>;
  // TVG-81: opt-in drag-and-drop row reordering. Off by default so every existing
  // table is untouched. When on, a drag handle appears and dropping a row emits
  // the full ordered list of row ids.
  @Input() reorderable = false;
  @Output() route = new EventEmitter<RouteParams>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() selectionChange = new EventEmitter<{ item: T; checked: boolean }>();
  @Output() reorder = new EventEmitter<(number | string)[]>();

  protected showPerPageControl: FormControl = new FormControl(100);
  protected readonly EditIcon = Edit;
  protected readonly EmptyIcon = Sparkles;
  protected readonly MinusIcon = Minus;
  protected readonly LoaderIcon = LoaderCircle;
  protected readonly GripIcon = GripVertical;

  private tooltipElement: HTMLElement | null = null;

  protected colsNum?: number;

  currentPage!: number;
  totalPages!: number;
  startEntry!: number;
  endEntry!: number;
  totalEntries!: number;
  pagesAroundCurrent: number[] = [];

  routeParams = new RouteParams({
    sort: [],
    filter: [],
    pageNum: 1,
    pageSize: 100,
  });

  perPageChoices = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  constructor(
    private router: Router,
    private renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataPage']) {
      this.loading = false;
      this.colsNum = this.visibleColumns.length + (this.rowActions ? 1 : 0);
      this.updatePagination();
    }
  }

  ngOnInit() {
    if (!this.paginate) {
      return;
    }

    this.showPerPageControl.valueChanges
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((perPage) => {
        this.loading = true;
        this.routeParams.pageSize = perPage;
        this.route.emit(this.routeParams);
      });
  }

  updatePagination() {
    if (!this.dataPage || !this.paginate) {
      return;
    }
    const meta: PageMetadata = this.dataPage.meta;
    this.currentPage = meta.current_page;
    this.totalPages = meta.last_page;
    this.startEntry = meta.from;
    this.endEntry = meta.to;
    this.totalEntries = meta.total;
    this.pagesAroundCurrent = this.getPagesAroundCurrent();
  }

  getPagesAroundCurrent(): number[] {
    const pages: number[] = [];
    const startPage = Math.max(2, this.currentPage - 2);
    const endPage = Math.min(this.totalPages - 1, this.currentPage + 2);
    for (let page = startPage; page <= endPage; page++) {
      pages.push(page);
    }
    return pages;
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.goToPage(this.currentPage);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.goToPage(this.currentPage);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;

      this.loading = true;
      this.routeParams.pageNum = this.currentPage;
      this.routeParams.pageSize = this.showPerPageControl.value;
      this.route.emit(this.routeParams);
    }
  }

  toggleSort(column: Column<T>) {
    const sortField = column.sortSource || column.field;
    const sort = this.routeParams.sort?.find((s) => s.field === sortField);

    if (sort) {
      if (sort.dir === 'asc') {
        sort.dir = 'desc';
      } else if (sort.dir === 'desc') {
        this.routeParams.sort = this.routeParams.sort?.filter((s) => s.field !== sortField);
      } else {
        sort.dir = 'asc';
      }
    } else {
      this.routeParams.sort?.push({ field: sortField, dir: 'asc' });
    }

    this.loading = true;
    this.route.emit(this.routeParams);
  }

  sortIcon(column: Column<T>): any {
    const sortField = column.sortSource || column.field;
    const sort = this.routeParams.sort?.find((s) => s.field === sortField);
    const direction = sort?.dir;

    if (column.type == 'number') {
      if (direction == 'asc') {
        return ArrowDown01;
      } else if (direction == 'desc') {
        return ArrowDown10;
      } else {
        return ArrowUpDown;
      }
    }
    if (column.type == 'date') {
      if (direction == 'asc') {
        return ArrowDownNarrowWide;
      } else if (direction == 'desc') {
        return ArrowDownWideNarrow;
      } else {
        return ArrowUpDown;
      }
    } else {
      if (direction == 'asc') {
        return ArrowDownAZ;
      } else if (direction == 'desc') {
        return ArrowDownZA;
      } else {
        return ArrowUpDown;
      }
    }
  }

  getValue(item: any, column: Column<T>) {
    if (column.callback) {
      return column.callback(item);
    } else if (column.type == 'date') {
      if (!item[column.field]) {
        return '';
      }
      const date = new Date(item[column.field]);
      return date.toLocaleDateString(column.dateFormat ?? 'en-GB');
    } else {
      return item[column.field];
    }
  }

  generateRouterLink(template: string, item: any): string {
    return template.replace(/:([a-zA-Z_]+)/g, (_, key) => item[key] || '');
  }

  componentInputs(column: Column<T>, item: T): Record<string, unknown> {
    return { item, ...(column.inputs ?? {}) };
  }

  mobileComponentInputs(column: Column<T>, item: T): Record<string, unknown> {
    return { item, ...(column.inputs ?? {}), ...(column.mobileInputs ?? {}) };
  }

  isRowInteractive(): boolean {
    return Boolean(this.rowClickable || this.rowRouterLinkTemplate);
  }

  isSelected(item: T): boolean {
    return item.id !== null && this.selectedRowIds.has(Number(item.id));
  }

  onSelectionChange(item: T, checked: boolean) {
    this.selectionChange.emit({ item, checked });
  }

  isExpanded(item: T): boolean {
    return this.selectedRowId !== undefined && item.id === this.selectedRowId;
  }

  // TVG-81: reorder the current page's rows and emit the resulting id order so
  // the host can persist it. Guarded by `reorderable`.
  onRowDropped(event: CdkDragDrop<T[]>) {
    if (!this.reorderable || !this.dataPage?.data) {
      return;
    }

    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.dataPage.data, event.previousIndex, event.currentIndex);
    const orderedIds = this.dataPage.data
      .map((row) => row.id)
      .filter((id): id is number => id !== null);
    this.reorder.emit(orderedIds);
  }

  reorderExtraColspan(): number {
    return this.reorderable ? 1 : 0;
  }

  expandedColspan(): number {
    return this.visibleColumns.length
      + (this.rowActions.length > 0 ? 1 : 0)
      + (this.selectionEnabled ? 1 : 0)
      + (this.reorderable ? 1 : 0);
  }

  handleRoute(item: any) {
    this.router.navigateByUrl(this.generateRouterLink(this.rowRouterLinkTemplate || '', item));
  }

  onRowClick(item: any) {
    this.rowClick.emit(item);
    if (this.rowRouterLinkTemplate) {
      this.handleRoute(item);
    }
  }


  hideTooltip() {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  ngOnDestroy() {
    this.hideTooltip();
  }
}
